import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../../store';
import Avatar, { AvatarGroup } from './Avatar';
import { Check, ChevronDown, X, UserCircle2 } from 'lucide-react';

/**
 * AssigneePicker
 * Props:
 *   value: string[]  — array of user _ids currently assigned
 *   onChange: (ids: string[]) => void
 *   members: { user: { _id, name, email, color, avatar }, role }[]  — project members
 *   size?: 'sm' | 'md'
 */
export default function AssigneePicker({ value = [], onChange, members = [], size = 'md' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  console.log('AssigneePicker render', { value, members });
  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (userId) => {
    if (value.includes(userId)) onChange(value.filter(id => id !== userId));
    else onChange([...value, userId]);
  };

  const selectedUsers = members
    .map(m => m.user)
    .filter(u => u && value.includes(u._id));

  return (
    <div className="relative inline-block" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
      >
        {selectedUsers.length > 0 ? (
          <AvatarGroup users={selectedUsers} max={3} />
        ) : (
          <UserCircle2 className="w-4 h-4 text-gray-400" />
        )}
        <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl animate-scale-in"
          style={{ minWidth: 200 }}>
          <div className="p-1.5">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider px-2 py-1">Исполнители</p>

            {members.length === 0 && (
              <p className="text-xs text-gray-400 px-2 py-2">Нет участников в проекте</p>
            )}

            {members.map(({ user: u }) => {
              if (!u) return null;
              const selected = value.includes(u._id);
              return (
                <button key={u._id} type="button"
                  onClick={() => toggle(u._id)}
                  className={`flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-left transition-colors ${selected ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  <Avatar name={u.name} color={u.color} src={u.avatar} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[10px] text-gray-400 truncate">{u.email}</div>
                  </div>
                  {selected && <Check className="w-3.5 h-3.5 text-brand-600 flex-shrink-0" />}
                </button>
              );
            })}

            {selectedUsers.length > 0 && (
              <>
                <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                <button type="button" onClick={() => onChange([])}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                  <X className="w-3 h-3" /> Снять всех
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
